import React, { useState, useEffect, useCallback } from 'react';
import { Clock, RefreshCw, Filter, ChevronDown, ChevronUp, CheckCircle2, XCircle, AlertCircle, Pencil, ArrowRight, Upload, Trash2, RotateCcw, FolderInput, UserPlus } from 'lucide-react';
import { DOC_CATEGORIES } from '../../data/mockData';

// category id ('bank_statements') -> human label ('Bank Statements'), for the
// grouped-upload row title below. Falls back to the raw id for a category
// somehow not in this list, rather than rendering blank.
const CATEGORY_LABEL = Object.fromEntries(DOC_CATEGORIES.map(c => [c.id, c.label]));

// Tailwind classes are spelled out in full per action (not built with template
// strings) so the JIT compiler, which scans for literal class names, actually
// includes them in the build.
const DOCUMENT_ACTION_META = {
  uploaded:            { verb: 'Uploaded',             icon: Upload,      bg: 'bg-blue-50',    text: 'text-blue-600' },
  deleted:             { verb: 'Moved to Trash',       icon: Trash2,      bg: 'bg-red-50',     text: 'text-red-600' },
  restored:            { verb: 'Restored',             icon: RotateCcw,   bg: 'bg-emerald-50', text: 'text-emerald-600' },
  permanently_deleted: { verb: 'Permanently Deleted',  icon: Trash2,      bg: 'bg-red-50',     text: 'text-red-700' },
  recategorized:       { verb: 'Recategorized',        icon: FolderInput, bg: 'bg-amber-50',   text: 'text-amber-600' },
};

// Which emoji leads a "X Updated Client Info" row, keyed by the same e.source
// strings the three writers actually send (clients.js's PATCH handler for
// 'Client Information'/'Link Data', autoSubmit.js's own logActivity call for
// 'Auto Submit'). Anything else - including the default, unset case - falls
// back to the person emoji, since a plain Client Information edit is a
// person typing into a form.
const EDIT_SOURCE_EMOJI = {
  'Auto Submit': '🔄',
  'Link Data':   '🔗',
  'Secondary Owner Cleared': '🧹',
};

const ORBIT_API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';

const TIMEFRAMES = [
  { label: 'All Time',   days: 0   },
  { label: 'Last 7d',   days: 7   },
  { label: 'Last 30d',  days: 30  },
  { label: 'Last 90d',  days: 90  },
];

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function elapsed(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d`;
}

export default function AuditTrail({ clientId }) {
  const [entries, setEntries]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [timeframe, setTimeframe]   = useState(0);
  const [lenderFilter, setLender]   = useState('');
  const [submitterFilter, setSub]   = useState('');
  const [expanded, setExpanded]     = useState(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem('mca_token');
      // Fetch submission log + activity log in parallel
      const [subRes, actRes] = await Promise.all([
        fetch(`${ORBIT_API}/auto-submit/log/${clientId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${ORBIT_API}/activity`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const subData = subRes.ok ? await subRes.json() : { entries: [] };
      const actData = actRes.ok ? await actRes.json() : [];
      // Filter activity to assignment and client-info-edit events for this client
      const actList = Array.isArray(actData) ? actData : [];
      const assignments = actList
        .filter(a => a.eventType === 'assignment' && a.clientId === clientId)
        .map(a => ({ ...a, _type: 'assignment' }));
      const edits = actList
        .filter(a => a.eventType === 'client_info_edit' && a.clientId === clientId)
        .map(a => ({ ...a, _type: 'edit' }));
      const documentActions = actList
        .filter(a => a.eventType === 'document_action' && a.clientId === clientId)
        .map(a => ({ ...a, _type: 'document' }));
      const created = actList
        .filter(a => a.eventType === 'client_created' && a.clientId === clientId)
        .map(a => ({ ...a, _type: 'created' }));
      const submissions = (subData.entries || []).map(e => ({ ...e, _type: 'submission' }));
      // Merge and sort by date descending
      const merged = [...submissions, ...assignments, ...edits, ...documentActions, ...created].sort((a, b) =>
        new Date(b.submittedAt || b.timestamp || 0) - new Date(a.submittedAt || a.timestamp || 0)
      );
      setEntries(merged);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  // derive filter options
  const allLenders    = [...new Set(entries.flatMap(e => e.lenders || []))].sort();
  const allSubmitters = [...new Set(entries.map(e => e.submittedBy).filter(Boolean))].sort();

  const cutoff = timeframe > 0 ? Date.now() - timeframe * 86400000 : 0;

  const filtered = entries.filter(e => {
    if (cutoff && new Date(e.submittedAt || e.timestamp).getTime() < cutoff) return false;
    if (lenderFilter && !(e.lenders || []).includes(lenderFilter)) return false;
    if (submitterFilter && e.submittedBy !== submitterFilter) return false;
    return true;
  });

  // stats - scoped to actual submissions so assignment/edit entries don't dilute the averages
  const filteredSubs   = filtered.filter(e => e._type === 'submission');
  const totalLenders   = filteredSubs.reduce((s, e) => s + (e.lenders?.length || 0), 0);
  const uniqueAccounts = new Set(filteredSubs.map(e => e.submittedBy)).size;
  const avgLenders     = filteredSubs.length ? (totalLenders / filteredSubs.length).toFixed(1) : '—';

  return (
    <div className="p-4 space-y-4">

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Submissions', value: filteredSubs.length },
          { label: 'Total Lenders', value: totalLenders },
          { label: 'Avg Lenders/Sub', value: avgLenders },
        ].map(s => (
          <div key={s.label} className="bg-white border border-gray-100 rounded-xl p-3 text-center shadow-sm">
            <p className="text-xl font-bold text-[#1e3a5f]">{s.value}</p>
            <p className="text-[11px] text-gray-400 font-medium">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-3 flex flex-wrap gap-2 items-center">
        <Filter size={13} className="text-gray-400 shrink-0" />

        {/* Timeframe */}
        <div className="flex gap-1">
          {TIMEFRAMES.map(t => (
            <button key={t.days} onClick={() => setTimeframe(t.days)}
              className={`text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors ${timeframe === t.days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Lender filter */}
        <select value={lenderFilter} onChange={e => setLender(e.target.value)}
          className="text-xs bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-gray-700 focus:outline-none">
          <option value="">All Lenders</option>
          {allLenders.map(l => <option key={l}>{l}</option>)}
        </select>

        {/* Submitter filter */}
        <select value={submitterFilter} onChange={e => setSub(e.target.value)}
          className="text-xs bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-gray-700 focus:outline-none">
          <option value="">All Submitters</option>
          {allSubmitters.map(s => <option key={s}>{s}</option>)}
        </select>

        <button onClick={load} disabled={loading}
          className="ml-auto flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && <p className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}

      {/* Entries */}
      {loading && <p className="text-xs text-gray-400 text-center py-6">Loading…</p>}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-10 text-gray-400">
          <Clock size={28} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">No submission history yet</p>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((e, i) => {
          const isOpen = expanded === i;
          const isAssignment = e._type === 'assignment';
          const isEdit = e._type === 'edit';
          const isDocument = e._type === 'document';
          const isCreated = e._type === 'created';
          const docMeta = isDocument ? (DOCUMENT_ACTION_META[e.action] || DOCUMENT_ACTION_META.uploaded) : null;
          // results is per-lender {lender, status, error?}; older entries
          // before this field existed just show as a flat, all-success list.
          const results = (!isAssignment && !isEdit && !isDocument && !isCreated)
            ? (e.results && e.results.length ? e.results : (e.lenders || []).map(l => ({ lender: l, status: 'success' })))
            : [];
          const failCount = results.filter(r => r.status === 'failed').length;
          const hasFailures = failCount > 0;
          const allFailed = results.length > 0 && failCount === results.length;
          // A lender that answered but said no (OnDeck's early cut). Shopped
          // and complete - counted separately from a failure so it never
          // reads as something that still needs fixing.
          const declinedResults = results.filter(r => r.status === 'declined');
          // The first real error/decline text, surfaced on the COLLAPSED row.
          // It used to live only inside the expanded panel, so a rep scanning
          // the trail saw "Submission Failed" with no idea what to fix
          // without opening every row one at a time.
          const firstProblem = results.find(r => (r.status === 'failed' || r.status === 'declined') && r.error);
          // channel 'submitted' is written by Confirm & Submit and Send to
          // Admins alike - a deal recorded with a shortlist of lenders, but
          // nothing has actually gone out to any of them yet (see performSubmit
          // and handleSendToAdmins in AutoSubmit.jsx, and submitLendersForClient
          // server-side, which is the only thing that ever writes any other
          // channel value). Everything else means a real send happened.
          // The Zoho Lenders-subform write is logged as its own entry
          // (channel 'crm-subform', written by classifyAndNotifyLenderOutcome
          // once the whole deal is out) rather than folded into the send it
          // belongs to - the write happens long after the send, so the send's
          // own row could never have carried its result.
          const isCrmSubform = !isAssignment && !isEdit && !isDocument && !isCreated && e.channel === 'crm-subform';
          const crmAllOk = isCrmSubform && results.length > 0 && results.every(r => r.status === 'success');
          const isRecordedOnly = !isAssignment && !isEdit && !isDocument && !isCreated && e.channel === 'submitted';
          // A recorded-only entry that ALSO carries an adminQueue marker is
          // not the rep's original submission - it's routeApiLenderToAdminQueue
          // server-side, writing its own entry for one API/Portal-Submission
          // lender an ultra admin's Send to Lenders click couldn't fire
          // itself. Distinct enough from a plain "recorded, nothing sent yet"
          // row to deserve its own label rather than reading as a second
          // "New Deal Submitted" for a deal that was submitted once already.
          const isAdminQueueRoute = isRecordedOnly && !!e.adminQueue;
          return (
            <div key={i} className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
              <button onClick={() => setExpanded(isOpen ? null : i)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isAssignment ? 'bg-purple-50' : isEdit ? 'bg-indigo-50' : isDocument ? docMeta.bg : isCreated ? 'bg-emerald-50' : isAdminQueueRoute ? 'bg-amber-50' : isRecordedOnly ? 'bg-gray-100' : hasFailures ? 'bg-red-50' : 'bg-blue-50'}`}>
                    {isAssignment
                      ? <Clock size={14} className="text-purple-600" />
                      : isEdit
                        ? <Pencil size={14} className="text-indigo-600" />
                        : isDocument
                          ? <docMeta.icon size={14} className={docMeta.text} />
                          : isCreated
                            ? <UserPlus size={14} className="text-emerald-600" />
                            : isAdminQueueRoute
                              ? <Clock size={14} className="text-amber-600" />
                              : isRecordedOnly
                                ? <Clock size={14} className="text-gray-500" />
                                : hasFailures
                                  ? <XCircle size={14} className="text-red-600" />
                                  : <CheckCircle2 size={14} className="text-blue-600" />}
                  </div>
                  <div className="min-w-0">
                    {isAssignment ? (
                      <>
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          👤 Rep Assignment
                        </p>
                        <p className="text-[11px] text-gray-400">{fmt(e.timestamp)} · by <span className="font-medium text-gray-600">{e.assignedBy || '—'}</span></p>
                        <p className="text-[10px] text-purple-700 font-medium mt-0.5">
                          {e.fromRepName || 'Unassigned'} → {e.toRepName}
                        </p>
                      </>
                    ) : isCreated ? (
                      <>
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          🆕 Client Manually Created
                        </p>
                        <p className="text-[11px] text-gray-400">{fmt(e.timestamp)} · by <span className="font-medium text-gray-600">{e.createdBy || '—'}</span></p>
                        <p className="text-[10px] text-emerald-700 font-medium mt-0.5">
                          No DocuSign envelope behind this client - Client Information won't auto-populate.
                        </p>
                      </>
                    ) : isDocument ? (
                      <>
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          {/* fileNames (plural) is the grouped-batch shape - one
                              row for the whole upload instead of one per file.
                              Older entries, and every non-upload document
                              action, still only ever carry the singular
                              fileName, so both keep working side by side. */}
                          {Array.isArray(e.fileNames) && e.fileNames.length > 1
                            ? `${e.fileNames.length} ${CATEGORY_LABEL[e.category] || e.category} ${docMeta.verb}`
                            : e.fileName || (e.fileNames || [])[0]
                              ? `${e.fileName || e.fileNames[0]} ${docMeta.verb}`
                              : docMeta.verb}
                        </p>
                        <p className="text-[11px] text-gray-400">{fmt(e.timestamp)} · by <span className="font-medium text-gray-600">{e.performedBy || '—'}</span></p>
                        {e.action === 'recategorized' && (
                          <p className={`text-[10px] font-medium mt-0.5 ${docMeta.text}`}>{e.fromCategory} → {e.toCategory}</p>
                        )}
                        {Array.isArray(e.fileNames) && e.fileNames.length > 1 && (
                          <p className="text-[10px] text-gray-500 mt-0.5 truncate">{e.fileNames.join(', ')}</p>
                        )}
                      </>
                    ) : isEdit ? (
                      <>
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          {/* Source leads so an edit made from Auto Submit (or
                              anywhere else that isn't the Client Information
                              page itself) doesn't read as if it happened
                              there - "Client Info Updated via X" put the
                              actual source last and easy to miss. */}
                          {EDIT_SOURCE_EMOJI[e.source] || '👤'} {e.source === 'Secondary Owner Cleared' ? 'Secondary Owner Cleared'
                            : e.source && e.source !== 'Client Information' ? `${e.source} Updated Client Info` : 'Client Info Updated'}
                        </p>
                        <p className="text-[11px] text-gray-400">{fmt(e.timestamp)} · by <span className="font-medium text-gray-600">{e.editedBy || '—'}</span></p>
                        <p className="text-[10px] text-indigo-700 font-medium mt-0.5">
                          {(e.changes || []).length} field{(e.changes || []).length === 1 ? '' : 's'} changed
                        </p>
                      </>
                    ) : isAdminQueueRoute ? (
                      <>
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          Routed to Admin Queue
                        </p>
                        <p className="text-[11px] text-gray-400">{fmt(e.submittedAt)} · by <span className="font-medium text-gray-600">{e.submittedBy || '—'}</span></p>
                        <p className="text-[10px] text-amber-700 font-medium mt-0.5">{(e.lenders || []).join(', ')} — needs a super admin to send</p>
                      </>
                    ) : isCrmSubform ? (
                      <>
                        {/* The Zoho Lenders-subform write is its own step, run
                            well after the send (classifyAndNotifyLenderOutcome,
                            once the whole deal is out), so it gets its own row
                            rather than riding along on the send's. */}
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          {crmAllOk ? 'Added to CRM' : 'CRM Update Failed'}
                        </p>
                        <p className="text-[11px] text-gray-400">{fmt(e.submittedAt)} · by <span className="font-medium text-gray-600">{e.submittedBy || '—'}</span></p>
                        <p className={`text-[10px] font-medium mt-0.5 ${crmAllOk ? 'text-emerald-600' : 'text-red-600'}`}>
                          {results.map(r => r.lender).join(', ')}
                          {results[0]?.subform?.zohoId ? ` → Zoho ${results[0].subform.zohoId}` : ''}
                        </p>
                        {firstProblem && (
                          <p className="text-[10px] mt-0.5 break-words text-red-600">{firstProblem.error}</p>
                        )}
                      </>
                    ) : isRecordedOnly ? (
                      <>
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          New Deal Submitted
                        </p>
                        <p className="text-[11px] text-gray-400">{fmt(e.submittedAt)} · by <span className="font-medium text-gray-600">{e.submittedBy || '—'}</span></p>
                      </>
                    ) : (
                      <>
                        {/* /trigger now writes one entry per lender as it
                            resolves (logLenderAttempt server-side), so an
                            entry where everything failed is a real, common
                            shape - "Sent to UW · 1 failed" read as though it
                            had gone out anyway. */}
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          {allFailed ? 'Submission Failed' : 'Sent to UW'}
                          {hasFailures && !allFailed && <span className="text-red-600"> · {failCount} failed</span>}
                          {declinedResults.length > 0 && <span className="text-amber-600"> · {declinedResults.length} declined</span>}
                        </p>
                        <p className="text-[11px] text-gray-400">{fmt(e.submittedAt)} · by <span className="font-medium text-gray-600">{e.submittedBy || '—'}</span></p>
                        {results.length > 0 && (
                          <p className={`text-[10px] font-medium mt-0.5 ${allFailed ? 'text-red-600' : 'text-amber-700'}`}>
                            {allFailed ? '✗' : '⚠ Shopped:'} {results.map(r => r.lender).join(', ')}
                          </p>
                        )}
                        {firstProblem && (
                          <p className={`text-[10px] mt-0.5 break-words ${firstProblem.status === 'failed' ? 'text-red-600' : 'text-amber-600'}`}>
                            {firstProblem.lender}: {firstProblem.error}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 font-medium">{elapsed(e.submittedAt || e.timestamp)} ago</span>
                  {isOpen ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                </div>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 border-t border-gray-50 pt-3 space-y-3">
                  {/* Lenders. For a recorded-only entry (channel 'submitted'),
                      results is just the shortlist with every entry defaulted
                      to status 'success' (see the fallback above) - nothing
                      was actually attempted, so the per-lender check/fail
                      icons and CRM subform outcome would be flat-out wrong
                      here. Rendered as a plain selected-lenders list instead. */}
                  {isEdit && (
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Changes</p>
                      <div className="space-y-1">
                        {(e.changes || []).map((c, ci) => (
                          <div key={ci} className="flex items-center gap-2 text-xs rounded-lg px-2.5 py-1.5 border bg-indigo-50 border-indigo-100">
                            <span className="font-semibold text-indigo-800 shrink-0">{c.field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</span>
                            <span className="text-gray-500 truncate">{c.from || '(blank)'}</span>
                            <ArrowRight size={11} className="text-gray-400 shrink-0" />
                            <span className="text-gray-800 font-medium truncate">{c.to || '(blank)'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {isDocument && (
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Details</p>
                      <p className="text-xs text-gray-600">{e.description}</p>
                    </div>
                  )}

                  {isCreated && (
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Details</p>
                      <p className="text-xs text-gray-600">{e.description}</p>
                    </div>
                  )}

                  {!isAssignment && !isEdit && !isDocument && !isCreated && (
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                        {isRecordedOnly ? 'Lenders Selected' : 'Lenders'}
                      </p>
                      {isRecordedOnly ? (
                        <div className="flex flex-wrap gap-1.5">
                          {results.map((r, ri) => (
                            <span key={ri} className="text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1">{r.lender}</span>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {results.map((r, ri) => (
                            <div key={ri} className={`flex items-start gap-2 text-xs rounded-lg px-2.5 py-1.5 border ${r.status === 'failed' ? 'bg-red-50 border-red-100' : r.status === 'declined' ? 'bg-amber-50 border-amber-100' : 'bg-blue-50 border-blue-100'}`}>
                              {r.status === 'failed'
                                ? <XCircle size={13} className="text-red-500 shrink-0 mt-0.5" />
                                : r.status === 'declined'
                                  ? <AlertCircle size={13} className="text-amber-500 shrink-0 mt-0.5" />
                                  : <CheckCircle2 size={13} className="text-blue-500 shrink-0 mt-0.5" />}
                              <div className="min-w-0">
                                <span className={`font-semibold ${r.status === 'failed' ? 'text-red-700' : r.status === 'declined' ? 'text-amber-700' : 'text-blue-700'}`}>{r.lender}</span>
                                {r.channel && <span className="text-[10px] text-gray-400 ml-1.5">({r.channel === 'direct' ? 'direct API' : 'Power Automate'})</span>}
                                {/* A decline is a real answer from the lender, not
                                    a problem to fix - the deal counts as shopped
                                    to them and still completes. */}
                                {r.status === 'declined' && <span className="text-[10px] text-amber-600 ml-1.5 font-medium">declined — still counts as shopped</span>}
                                {(r.status === 'failed' || r.status === 'declined') && r.error && (
                                  <p className={`text-[11px] mt-0.5 break-words ${r.status === 'failed' ? 'text-red-600' : 'text-amber-700'}`}>{r.error}</p>
                                )}
                                {/* CRM Lenders-subform outcome - temporary, added while
                                    tracking down why the subform wasn't picking up every
                                    lender. r.subform is undefined on older entries logged
                                    before this field existed, so those just show nothing. */}
                                {r.subform && (
                                  <p className={`text-[10px] mt-0.5 ${r.subform.success ? 'text-emerald-600' : 'text-amber-600'}`}>
                                    CRM subform: {r.subform.success
                                      ? `appended (Zoho ${r.subform.zohoId})`
                                      : r.subform.reason === 'no-zoho-id'
                                        ? 'skipped - deal has no linked Zoho record'
                                        : `failed - ${r.subform.error || 'unknown error'}`}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Deal criteria */}
                  {e.dealCriteria && Object.values(e.dealCriteria).some(Boolean) && (
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Deal Criteria</p>
                      <div className="grid grid-cols-3 gap-2">
                        {Object.entries(e.dealCriteria).filter(([,v]) => v).map(([k,v]) => (
                          <div key={k}>
                            <p className="text-[10px] text-gray-400 capitalize">{k.replace(/([A-Z])/g,' $1')}</p>
                            <p className="text-xs font-medium text-gray-700">{v}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {e.notes && (
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Notes</p>
                      <p className="text-xs text-gray-600 whitespace-pre-wrap">{e.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
